/** BE TF ladder (fine → coarse); matches `resolve_candle_type_for_window`. */
export const EXECUTION_CANDLE_TYPE_LADDER = ["m", "5m", "15m", "30m", "h", "4h", "1d"] as const;

export type ExecutionCandleTypeToken = (typeof EXECUTION_CANDLE_TYPE_LADDER)[number];

/** Normalize API / UI candle_type tokens to ladder entries. */
export function normalizeExecutionCandleType(raw: string | undefined | null): string | null {
  if (!raw || typeof raw !== "string") return null;
  const t = raw.trim().toLowerCase();
  if (!t) return null;
  if (t === "1m" || t === "m") return "m";
  if (t === "1h" || t === "h") return "h";
  if (t === "2h") return "h";
  if (EXECUTION_CANDLE_TYPE_LADDER.includes(t as ExecutionCandleTypeToken)) return t;
  return t;
}

export function formatExecutionCandleTypeLabel(raw: string | undefined | null): string {
  const n = normalizeExecutionCandleType(raw);
  if (!n) return "";
  if (n === "m") return "1m";
  if (n === "h") return "1h";
  return n;
}

/**
 * Canonical `candle_type` query values for execution-candles / stats-service.
 * BE `ALLOWED_CANDLE_TYPES` uses `1m`, `1h`, … — bare `m` / `h` fall back to `1m`.
 */
export function toApiExecutionCandleType(raw: string | undefined | null): string | null {
  const n = normalizeExecutionCandleType(raw);
  if (!n) return null;
  if (n === "m") return "1m";
  if (n === "h") return "1h";
  return n;
}

export function executionCandleTypeRank(raw: string | undefined | null): number {
  const n = normalizeExecutionCandleType(raw);
  if (!n) return -1;
  const idx = EXECUTION_CANDLE_TYPE_LADDER.indexOf(n as ExecutionCandleTypeToken);
  return idx >= 0 ? idx : -1;
}

/** True when `a` is strictly finer (smaller bars) than `b`. */
export function isExecutionCandleTypeFinerThan(
  a: string | undefined | null,
  b: string | undefined | null
): boolean {
  const ra = executionCandleTypeRank(a);
  const rb = executionCandleTypeRank(b);
  if (ra < 0 || rb < 0) return false;
  return ra < rb;
}

export function executionCandleTypesMatch(
  a: string | undefined | null,
  b: string | undefined | null
): boolean {
  const na = normalizeExecutionCandleType(a);
  const nb = normalizeExecutionCandleType(b);
  if (!na || !nb) return !na && !nb;
  return na === nb;
}

/** Options finer than `floor` should be disabled in the candle-type menu. */
export function isExecutionCandleTypeDisabledForBarBudget(
  optionValue: string,
  floor: string | undefined | null
): boolean {
  const floorNorm = normalizeExecutionCandleType(floor);
  if (!floorNorm) return false;
  return isExecutionCandleTypeFinerThan(optionValue, floorNorm);
}

/**
 * Minimum TF to enforce in the candle menu after a coarsened execution-candles response.
 * `resolved_candle_type` echoes the active TF — not the window floor — so do not use it
 * unless BE actually coarsened the user's request.
 */
export function pickExecutionCandlesBarBudgetFloor(
  response: {
    resolved_candle_type?: string | null;
    resolved_window?: { resolved_candle_type?: string | null } | null;
    candle_type?: string | null;
    candle_type_coarsened?: boolean;
  } | null | undefined,
  requestedCandleType: string
): string | null {
  if (!response) return null;
  if (response.candle_type_coarsened === true) {
    return pickResolvedExecutionCandleType(response);
  }
  if (response.candle_type_coarsened === false) return null;
  const resolved = pickResolvedExecutionCandleType(response);
  const requested = pickRequestedExecutionCandleType(response, requestedCandleType);
  if (!resolved || !requested) return null;
  if (!isExecutionCandleTypeFinerThan(requested, resolved)) return null;
  return resolved;
}

export function pickResolvedExecutionCandleType(response: {
  resolved_candle_type?: string | null;
  resolved_window?: { resolved_candle_type?: string | null } | null;
  candle_type?: string | null;
} | null | undefined): string | null {
  if (!response) return null;
  return (
    normalizeExecutionCandleType(response.resolved_candle_type) ??
    normalizeExecutionCandleType(response.resolved_window?.resolved_candle_type) ??
    normalizeExecutionCandleType(response.candle_type)
  );
}

export function pickRequestedExecutionCandleType(
  response: { candle_type?: string | null } | null | undefined,
  fallbackUserSelection: string
): string {
  return normalizeExecutionCandleType(response?.candle_type) ?? normalizeExecutionCandleType(fallbackUserSelection) ?? fallbackUserSelection;
}
